import * as React from 'react'
import { Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Root from '@components/root/Root'
import Modal from '@components/modal/Modal'
import Button from '@components/button/Button'
import Icon from '@components/icon/Icons'
import SimpleAlert from '@components/modal/SimpleAlert'
import ModalLoader from '@components/modal/ModalLoader'

import Layout from './AdminPlanLayout'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'
import useReducer from '@store/useReducer'
import * as PlanStore from '@store/plan/plan'

import api from '@api/api'

import useWorkflow from './useWorkflow'
import { SCREENS } from './workflow'

interface Props {
  spaceId?: string
  formStep?: AdminPlanStep
}

const init: Partial<AdminPlanForm> = {
  space: '',
}

const AdminUserFormScreen = ({ spaceId, formStep }: Props) => {
  const i18n = useI18n()
  const navigation = useNavigation()

  const plans = useReducer(PlanStore.store, (s) => s.plans)

  const [form, setForm] = React.useState(init as AdminPlanForm)
  const [step, setStep] = React.useState(0)

  const [workflow] = useWorkflow(form, spaceId)

  React.useEffect(() => {
    if (spaceId) {
      const plan = plans.find((p) => p.localisationId === spaceId)

      if (plan) {
        setForm({ space: spaceId } as AdminPlanForm)
      }
    }
  }, [spaceId])

  React.useEffect(() => {
    const idx = workflow.findIndex((w) => w === formStep)

    if (idx > -1) {
      setStep(idx)
    }
  }, [formStep, workflow])

  const onForm = (item: Partial<AdminPlanForm>) => {
    setForm({ ...form, ...item })
    return Promise.resolve()
  }

  const onNext = () => {
    if (step === workflow.length - 1) {
      onFinish()
    } else {
      const next = workflow[step + 1] || ''
      navigation.push(`/admin/plans/form/${next}`)
    }
  }

  const onBack = () => {
    if (spaceId) {
      // no back for single change
      quit()
      return
    }

    if (step === 0) {
      navigation.goBack()
    } else {
      const previous = workflow[step - 1] || ''
      navigation.push(`/admin/plans/form/${previous}`)
    }
  }

  const quit = () => {
    if (spaceId) {
      navigation.jumpTo(`/admin/plans/${spaceId}`)
    } else {
      navigation.jumpTo(`/admin/plans`)
    }
  }

  const finish = () => {
    Modal.close()
    quit()
  }

  const reset = () => {
    Modal.close()
    setStep(0)
    setForm(init as AdminPlanForm)
  }

  const onFinish = () => {
    ModalLoader.open({ title: i18n.t('screens.admin.plans.form.sending') })

    const data = form.map.data.split(';base64,')[1]

    if (spaceId) {
      api.plan
        .update(spaceId, { dxfBase64: data })
        .then(() => {
          ModalLoader.close()
          finish()
        })
        .catch(() => {
          ModalLoader.close()

          SimpleAlert.open({
            title: i18n.t('common.error'),
            description: i18n.t('screens.admin.plans.form.sendingFailed'),
            buttons: [{ label: i18n.t('common.ok'), onClick: () => SimpleAlert.close() }],
          })
        })
    } else {
      api.plan
        .create(form.space, { dxfBase64: data })
        .then(() => {
          ModalLoader.close()
          Modal.open({ Content: renderFinish, cancel: false })
        })
        .catch(() => {
          ModalLoader.close()

          SimpleAlert.open({
            title: i18n.t('common.error'),
            description: i18n.t('screens.admin.plans.form.sendingFailed'),
            buttons: [{ label: i18n.t('common.ok'), onClick: () => SimpleAlert.close() }],
          })
        })
    }
  }

  const renderFinish = () => {
    return (
      <FinishContainer>
        <Title>{i18n.t('screens.admin.plans.form.sent')}</Title>
        <Info>{i18n.t('screens.admin.plans.form.sentInfo')}</Info>
        <Buttons>
          <ButtonContainer>
            <Button label={i18n.t('screens.admin.plans.form.title')} onClick={reset} color="light" status="active" />
          </ButtonContainer>
          <ButtonContainer>
            <Button label={i18n.t('common.finish')} onClick={finish} status="active" />
          </ButtonContainer>
        </Buttons>

        <Close activeOpacity={0.8} onPress={finish}>
          <Icon name="close" color={theme.colors.primary} size={25} />
        </Close>
      </FinishContainer>
    )
  }

  const screen = workflow[step]
  const FormScreen = SCREENS[screen]

  if (!screen) {
    return <Root left="close" onLeft={quit} header={i18n.t('screens.admin.plans.form.title')} fixed loading />
  }

  return (
    <Root left="close" onLeft={quit} header={i18n.t('screens.admin.plans.form.title')} fixed>
      {
        <ItemContainer key={screen}>
          <Layout step={screen} form={form} onBack={onBack} onNext={onNext} onQuit={quit}>
            <FormScreen form={form} onForm={onForm} onBack={onBack} />
          </Layout>
        </ItemContainer>
      }
    </Root>
  )
}

export default AdminUserFormScreen

const ItemContainer = styled(View)`
  flex: 1;
`

const FinishContainer = styled(View)`
  position: relative;
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? '24px 16px;' : '50px 140px')};
  max-width: 880px;
  background-color: ${(props) => props.theme.colors.background};
`

const Close = styled(TouchableOpacity)`
  position: absolute;
  top: ${(props) => (props.theme.breakpoint === 'phone' ? '0px' : '15px')};
  right: ${(props) => (props.theme.breakpoint === 'phone' ? '0px' : '15px')};
  padding: 15px;
`

const Title = styled(Text)`
  ${(props) => props.theme.fonts.Title1};
  font-size: 24px;
  margin-bottom: 24px;
  text-align: center;
`

const Info = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.secondaryText};
  text-align: center;
`

const Buttons = styled(View)`
  flex-direction: ${(props) => (props.theme.breakpoint === 'phone' ? 'column' : 'row')};
  margin-top: 38px;
`

const ButtonContainer = styled(View)`
  ${(props) => props.theme.breakpoint !== 'phone' && `width: 270px;`}
  margin: 10px;
`
